/**
 * The browser's wallet boundary: connect a wallet onto Arc, and - for the feed
 * flow - burn OBRAIN into the dead address by calling `feed(channel, tier)`.
 *
 * Two flows with two different rules meet here:
 *   - the launchpad's hatch flow only reads, and ends at the UNSIGNED prepared
 *     transaction;
 *   - feeding signs, because a burn is the visitor's own money. The page never
 *     holds a key: every write below hands calldata to the visitor's wallet and
 *     the wallet - not this module - is what asks for the signature.
 *
 * No wallet library: the four calldata payloads are hand-encoded here, which is
 * cheaper than a dependency and easy to check against the contracts' selectors
 * (`cast sig "feed(uint8,uint8)"` = 0xf1ce990a, and the three ERC-20 ones).
 */

type Wallet = {
  readonly address: string;
  readonly chainId: number;
};

import { asArray, asNumber, asString, optionalField, parseJson, type JsonValue } from "./json";

/** JSON-RPC replies are JSON, so the provider boundary is typed as JSON. */
type Provider = {
  request(args: { method: string; params?: JsonValue[] }): Promise<JsonValue>;
  on?(event: string, handler: (...args: never[]) => void): void;
};

declare global {
  interface Window {
    ethereum?: Provider;
  }
}

/** The chain the product runs on. */
const CHAIN = {
  id: 5042,
  hex: "0x13b2",
  name: "Arc",
  rpc: "https://rpc.mainnet.arc.io",
  explorer: "https://explorer.arc.io",
  symbol: "USDC",
  decimals: 18,
  token: "0x28f986a61e078795639f239675582a12b4cf7f01",
} as const;

const SELECTOR_APPROVE = "095ea7b3";

const SELECTOR_FEED = "f1ce990a";

/** NeuronRegistry.register(uint256,bytes32,address), the claim the collection reads. */
const SELECTOR_REGISTER = "e6e2a434";

/** SpecimenCard.mint(uint256,bytes32,uint256): the card's biology rides the mint. */
const SELECTOR_MINT = "6fa13699";

/** The deployed SpecimenCard: one neuron, one card, forever (ownerless). */
export const CARD_ADDRESS = "0x3f5415c08f758dbe83d6d6c0336a761e3a8b35ae";

const SELECTOR_BALANCE = "70a08231";

const SELECTOR_ALLOWANCE = "dd62ed3e";

function padUint(value: bigint | number): string {
  return BigInt(value).toString(16).padStart(64, "0");
}

function padAddress(address: string): string {
  return address.replace(/^0x/, "").toLowerCase().padStart(64, "0");
}

function provider(): Provider | null {
  return window.ethereum ?? null;
}

async function raw(
  provider: Provider,
  method: string,
  params: JsonValue[] = [],
): Promise<JsonValue> {
  return provider.request({ method, params });
}

export async function connect(): Promise<Wallet> {
  const injected = provider();

  if (injected === null) throw new Error("no wallet found in this browser");

  const accounts = asArray(await raw(injected, "eth_requestAccounts"), "accounts");

  if (accounts.length === 0) throw new Error("wallet returned no account");

  await useArc(injected);

  return { address: asString(accounts[0], "accounts[0]"), chainId: CHAIN.id };
}

/**
 * Forget this origin in the wallet so the next connect opens the picker again.
 * EIP-1193 has no standard disconnect; revokePermissions is the MetaMask way and
 * a refusal is fine - the page drops its own state either way.
 */
export async function disconnect(): Promise<void> {
  const injected = provider();

  if (injected === null) return;

  try {
    await raw(injected, "wallet_revokePermissions", [{ eth_accounts: {} }]);
  } catch {
    // wallets without revokePermissions just keep the account authorized
  }
}

/** Switch to Arc mainnet, offering to add it when the wallet does not know it. */
async function useArc(injected: Provider): Promise<void> {
  try {
    await raw(injected, "wallet_switchEthereumChain", [{ chainId: CHAIN.hex }]);
  } catch (error) {
    // wallets signal "unknown chain" with a numeric code on the rejection
    const rejected = parseJson(JSON.stringify(error), "wallet rejection");
    const code = optionalField(rejected, "code", "wallet rejection");

    if (code === null || asNumber(code, "wallet rejection.code") !== 4902) throw error;

    await raw(injected, "wallet_addEthereumChain", [
      {
        chainId: CHAIN.hex,
        chainName: CHAIN.name,
        nativeCurrency: { name: CHAIN.symbol, symbol: CHAIN.symbol, decimals: CHAIN.decimals },
        rpcUrls: [CHAIN.rpc],
        blockExplorerUrls: [CHAIN.explorer],
      },
    ]);
  }
}

async function waitForReceipt(injected: Provider, hash: string): Promise<string> {
  for (let attempt = 0; attempt < 120; attempt += 1) {
    const receipt = await raw(injected, "eth_getTransactionReceipt", [hash]);

    if (receipt !== null) {
      const status = optionalField(receipt, "status", "receipt");

      if (status !== null && asString(status, "receipt.status") !== "0x1")
        throw new Error(`transaction ${hash} reverted`);

      return hash;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 1_000));
  }

  throw new Error(`transaction ${hash} is still pending`);
}

/** One transaction, as the wallet's RPC expects it: the call the visitor signs. */
type WalletCall = {
  readonly from: string;
  readonly to: string;
  readonly data: string;
  /** The native amount the call carries (the o1 creation fee), in wei. */
  value?: string;
};

/**
 * Broadcast one prepared call and wait for its receipt. `value` is the native
 * amount the call must carry (the o1 creation fee), in wei as the plan states
 * it; the four feed calldata payloads never carry any.
 */
async function send(
  injected: Provider,
  from: string,
  to: string,
  data: string,
  value?: string,
): Promise<string> {
  // the fee rides the call when the plan states one; a zero or absent value
  // stays off the transaction entirely, which is what the four feed calls want
  const call: WalletCall = { from, to, data };

  if (value !== undefined && BigInt(value) > 0n) {
    call.value = value;
  }

  const sent = await raw(injected, "eth_sendTransaction", [call]);

  return waitForReceipt(injected, asString(sent, "transaction hash"));
}

function wallet(): Provider {
  const injected = provider();

  if (injected === null) throw new Error("no wallet found in this browser");

  return injected;
}

/** One-time ERC-20 approval, so every later feed is a single transaction. */
export async function approve(
  from: string,
  token: string,
  spender: string,
  amount: bigint,
): Promise<string> {
  return send(
    wallet(),
    from,
    token,
    `0x${SELECTOR_APPROVE}${padAddress(spender)}${padUint(amount)}`,
  );
}

/** The wallet's OBRAIN balance, read straight from the token contract. */
export async function balanceOf(owner: string, token: string): Promise<bigint> {
  const result = await raw(wallet(), "eth_call", [
    { to: token, data: `0x${SELECTOR_BALANCE}${padAddress(owner)}` },
    "latest",
  ]);

  return BigInt(asString(result, "balanceOf"));
}

/** What the brain may still burn, read straight from the token contract. */
export async function allowance(owner: string, spender: string, token: string): Promise<bigint> {
  const result = await raw(wallet(), "eth_call", [
    { to: token, data: `0x${SELECTOR_ALLOWANCE}${padAddress(owner)}${padAddress(spender)}` },
    "latest",
  ]);

  return BigInt(asString(result, "allowance"));
}

/** Burn `tier` OBRAIN into the dead address and let the brain think about it. */
export async function feed(
  from: string,
  brain: string,
  channel: number,
  tier: number,
): Promise<string> {
  return send(wallet(), from, brain, `0x${SELECTOR_FEED}${padUint(channel)}${padUint(tier)}`);
}

/**
 * Sign and broadcast a call the o1 plan prepared - the hatch's `createLaunch`,
 * which deploys the token and opens its market, carrying the creation fee. The
 * page never signs: the visitor's wallet is handed the exact call the plan
 * returned, with the fee it stated, and does the signing itself.
 */
export async function sendCall(call: WalletCall): Promise<string> {
  return send(wallet(), call.from, call.to, call.data, call.value);
}

/**
 * Claim this neuron's hatch in the NeuronRegistry, the collection's one source
 * of rows. Permissionless by design, but one-shot per neuron, so the hatcher
 * who just paid should claim their own slot immediately. The calldata mirrors
 * the contract's `register(uint256,bytes32,address)` (selector 0xe6e2a434, the
 * selector the live CB180 registration carried).
 */
export async function registerClaim(
  from: string,
  registry: string,
  neuronId: number,
  thoughtTx: string,
  token: string,
): Promise<string> {
  const thought = thoughtTx.replace(/^0x/, "").toLowerCase().padStart(64, "0");

  return send(
    wallet(),
    from,
    registry,
    `0x${SELECTOR_REGISTER}${padUint(neuronId)}${thought}${padAddress(token)}`,
  );
}

/**
 * Mint this neuron's specimen card: the hatch's artifact since o1 was removed.
 * `tokenId` is the neuron id, so the card contract itself enforces one card per
 * neuron, ever. The thought and the biology (`traits`) travel with the card, and
 * the contract serves both in its own metadata.
 */
export async function mintCard(
  from: string,
  neuronId: number,
  thoughtTx: string,
  traits: bigint,
): Promise<string> {
  const thought = thoughtTx.replace(/^0x/, "").toLowerCase().padStart(64, "0");

  return send(
    wallet(),
    from,
    CARD_ADDRESS,
    `0x${SELECTOR_MINT}${padUint(neuronId)}${thought}${padUint(traits)}`,
  );
}

/** Whether an address carries code on this chain: the launch's own proof. */
export async function hasCode(address: string): Promise<boolean> {
  const code = await raw(wallet(), "eth_getCode", [address, "latest"]);

  return asString(code, "eth_getCode").length > 2;
}

export function shortAddress(address: string): string {
  return address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}
